import apiClient from './client'

export interface ElectricalPanel {
  id: number
  obj_id: number
  proj_id: string
  name: string
  description: string
  location: string
  created_at: string
  updated_at: string
}

export interface TerminalBlock {
  id: number
  electrical_panel_id: number
  name: string
  description: string
  position_in_panel: number
  schema_id: number | null
  instances_count?: number
  created_at: string
  updated_at: string
}

export const getElectricalPanels = async (projectId?: number): Promise<ElectricalPanel[]> => {
  try {
    const url = projectId 
      ? `/api/electrical_panels?project_id=${projectId}`
      : '/api/electrical_panels'
    
    const response = await apiClient.get(url)
    
    if (Array.isArray(response.data)) {
      return response.data as ElectricalPanel[]
    }
    
    return []
  } catch (error) {
    console.error('❌ Error fetching electrical panels:', error)
    return []
  }
}

export const getElectricalPanelById = async (id: number): Promise<ElectricalPanel | null> => {
  try {
    const response = await apiClient.get(`/api/electrical_panels/${id}`)
    return response.data as ElectricalPanel
  } catch (error) {
    console.error('❌ Error fetching electrical panel:', error)
    return null
  }
}

export const createElectricalPanel = async (panelData: {
  obj_id: number
  proj_id: string
  name: string
  description?: string
  location?: string
}): Promise<ElectricalPanel> => {
  const response = await apiClient.post('/api/electrical_panels', panelData)
  
  if ((response as any).data?.id) {
    const panel = await getElectricalPanelById(Number((response as any).data.id))
    return panel || ({} as ElectricalPanel)
  }
  
  if ((response as any).id) {
    const panel = await getElectricalPanelById(Number((response as any).id))
    return panel || ({} as ElectricalPanel)
  }
  
  return response.data as ElectricalPanel
}

export const updateElectricalPanel = async (
  id: number,
  panelData: {
    proj_id: string
    name: string
    description?: string
    location?: string
  }
): Promise<ElectricalPanel> => {
  await apiClient.put(`/api/electrical_panels/${id}`, panelData)
  
  const panel = await getElectricalPanelById(id)
  return panel || ({} as ElectricalPanel)
}

export const deleteElectricalPanel = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/electrical_panels/${id}`)
}

// Terminal Blocks API
export const getTerminalBlocks = async (electricalPanelId: number): Promise<TerminalBlock[]> => {
  try {
    const response = await apiClient.get(`/api/terminal_blocks?electrical_panel_id=${electricalPanelId}`)
    
    if (Array.isArray(response.data)) {
      return response.data as TerminalBlock[]
    }
    
    return []
  } catch (error) {
    console.error('❌ Error fetching terminal blocks:', error)
    return []
  }
}

export const getTerminalBlockById = async (id: number): Promise<TerminalBlock | null> => {
  try {
    const response = await apiClient.get(`/api/terminal_blocks/${id}`)
    return response.data as TerminalBlock
  } catch (error) {
    console.error('❌ Error fetching terminal block:', error)
    return null
  }
}

export const createTerminalBlock = async (blockData: {
  electrical_panel_id: number
  name: string
  description?: string
  position_in_panel?: number
}): Promise<TerminalBlock> => {
  const response = await apiClient.post('/api/terminal_blocks', blockData)
  return response.data as TerminalBlock
}

export const updateTerminalBlock = async (
  id: number,
  blockData: {
    name: string
    description?: string
    position_in_panel?: number
  }
): Promise<TerminalBlock> => {
  const response = await apiClient.put(`/api/terminal_blocks/${id}`, blockData)
  return response.data as TerminalBlock
}

export const deleteTerminalBlock = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/terminal_blocks/${id}`)
} 